/**
 * useCommunicationPatterns — TanStack Query hook for `communication.list`.
 *
 * Fetches LLM-derived comparisons between current WHO/CDC hantavirus statements
 * and publicly-documented COVID-era institutional communication patterns.
 *
 * Each row carries provenance anchors (covid_source_url + current_source_url)
 * that were HEAD-verified at ingest time. Date fields arrive as ISO strings
 * over the wire and are normalised to Date objects here.
 *
 * Prompt #08 (communication-patterns-covid-comparison).
 */

import { useQuery } from "@tanstack/react-query";

export type CommunicationPatternItem = {
  patternId: string;
  datedAt: Date;
  patternLabel: string;
  covidExample: string;
  currentExample: string;
  match: boolean;
  severity: "HIGH" | "MEDIUM";
  covidSourceUrl: string;
  currentSourceUrl: string;
  verifiedAt: Date;
};

type RawPattern = {
  patternId: string;
  datedAt: string;
  patternLabel: string;
  covidExample: string;
  currentExample: string;
  match: boolean;
  severity: "HIGH" | "MEDIUM";
  covidSourceUrl: string;
  currentSourceUrl: string;
  verifiedAt: string;
};

function normalise(raw: RawPattern): CommunicationPatternItem {
  return {
    ...raw,
    datedAt: new Date(raw.datedAt),
    verifiedAt: new Date(raw.verifiedAt),
  };
}

async function fetchCommunicationPatterns(
  limit: number,
  apiBaseUrl?: string,
): Promise<CommunicationPatternItem[]> {
  const base = (
    apiBaseUrl ??
    (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ??
    ""
  ).replace(/\/+$/u, "");

  const encoded = encodeURIComponent(
    JSON.stringify({ json: { limit } }),
  );
  const url = `${base}/api/trpc/communication.list?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });

  if (!res.ok) {
    throw new Error(`communication.list failed: ${res.status}`);
  }

  const json = (await res.json()) as {
    result?: { data?: { json?: RawPattern[] } | RawPattern[] };
  };

  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawPattern[] }).json
      : (inner as RawPattern[] | undefined);

  if (!data) throw new Error("communication.list: malformed response");
  return data.map(normalise);
}

export function useCommunicationPatterns(opts?: {
  limit?: number;
  apiBaseUrl?: string;
  locale?: string;
}) {
  const limit = opts?.limit ?? 25;

  return useQuery<CommunicationPatternItem[]>({
    queryKey: ["communication", "list", limit],
    queryFn: () => fetchCommunicationPatterns(limit, opts?.apiBaseUrl),
    staleTime: 4 * 60 * 60 * 1000, // 4 hours — matches the extractor cadence
    retry: 2,
  });
}
